<!-- @description: 扫码支付组件 -->
<script lang="ts" setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { PaymentOrderInfo } from '../../apis/types/payment'
import { formatAmount } from '@web-publish-id/shared'
import QRCode from 'qrcode'
import { getPaymentOrderInfo } from '../../apis/payment'
import { pollPaymentStatus } from '../../utils/paymentOrder'

const props = defineProps<{
  orderInfo: PaymentOrderInfo
  createOrder: () => Promise<any>
}>()

const emit = defineEmits<{
  (e: 'submit'): void
  (e: 'error', value: any): void
  (e: 'click-complete'): void
  (e: 'completed'): void
}>()

const loading = ref(false)
const qrCodeUrl = ref('')
const polling = ref(false)

// 二维码倒计时
const qrCodeCountdown = ref(0)

// 订单完成冷却时间
const completeCoolingTime = ref(0)
let qrCodeCountdownTimer: ReturnType<typeof setTimeout> | null = null
let completeCoolingTimer: ReturnType<typeof setInterval> | null = null
let pollController: AbortController | null = null
let completed = false
let disposed = false

// 支付金额
const paymentAmount = (() => {
  if (!props.orderInfo) return ''
  const { price, currency } = props.orderInfo
  if (!price || !currency) return ''
  return `${currency} ${formatAmount(price)}`
})()

// 二维码倒计时格式
const qrCodeCountdownFormat = computed(() => {
  const minutes = String(Math.floor(qrCodeCountdown.value / 60)).padStart(2, '0')
  const seconds = String(qrCodeCountdown.value % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})

// 开始二维码倒计时
const startQrCodeCountdown = (expireTimeAt: number) => {
  if (qrCodeCountdownTimer) clearTimeout(qrCodeCountdownTimer)

  // 纠正时间
  const correctTime = () => {
    const now = Date.now()
    qrCodeCountdown.value = Math.max(0, Math.floor((expireTimeAt - now) / 1000))

    if (qrCodeCountdown.value > 0) {
      qrCodeCountdownTimer = setTimeout(correctTime, 500)
    } else {
      qrCodeCountdownTimer = null
    }
  }

  correctTime()
}

const createQrCode = (url: string) => {
  return QRCode.toDataURL(url, {
    margin: 0,
    width: 160,
  })
}

const stopPolling = () => {
  pollController?.abort()
  pollController = null
  polling.value = false
}

const completePayment = () => {
  if (disposed || completed) return

  completed = true
  stopPolling()
  emit('completed')
}

const startPaymentStatusPolling = async (paymentId: string, expireTimeAt?: number) => {
  stopPolling()

  const remainingDuration = expireTimeAt ? Math.max(0, expireTimeAt - Date.now()) : undefined
  if (remainingDuration === 0) return

  const controller = new AbortController()
  pollController = controller
  polling.value = true

  const result = await pollPaymentStatus({
    payment_id: paymentId,
    maxDuration: remainingDuration,
    signal: controller.signal,
  })

  if (controller.signal.aborted || pollController !== controller) return

  pollController = null
  polling.value = false

  if (result.success) {
    completePayment()
    return
  }

  if (!result.timeout) {
    emit('error', new Error(result.paymentInfo?.validate_desc || 'Payment status verification failed'))
  }
}

const handlePay = async () => {
  if (disposed || loading.value || completed) return
  emit('submit')

  loading.value = true
  completed = false
  stopPolling()

  try {
    const result = await props.createOrder()
    if (disposed) return
    const resultData = result?.result_data

    // 使用本次支付订单返回的二维码和过期时间
    startQrCodeCountdown(resultData?.expire_time_at)

    const image = await createQrCode(resultData?.qr_code || '')
    if (disposed) return
    qrCodeUrl.value = image

    if (result?.payment_id) {
      void startPaymentStatusPolling(result.payment_id, resultData?.expire_time_at)
    }
  } catch (error) {
    if (disposed) return
    console.error(error)
    emit('error', error)
  } finally {
    loading.value = false
  }
}

// 检查订单状态
const checkOrderState = async () => {
  const res = await getPaymentOrderInfo({ sys_order_id: props.orderInfo.sys_order_id })
  const orderInfo = res?.order_info
  if (!orderInfo) {
    throw new Error('orderInfo is null')
  }

  return orderInfo
}

// 开始订单完成冷却时间
const startCompleteCoolingTime = () => {
  completeCoolingTime.value = 2
  if (completeCoolingTimer) clearInterval(completeCoolingTimer)

  completeCoolingTimer = setInterval(() => {
    completeCoolingTime.value--
    if (completeCoolingTime.value <= 0) {
      clearInterval(completeCoolingTimer!)
      completeCoolingTimer = null
    }
  }, 1000)
}

// 完成支付
const handleComplete = async () => {
  if (disposed || loading.value || completed) return
  emit('click-complete')

  loading.value = true
  try {
    const orderInfo = await checkOrderState()
    if (disposed) return
    if (orderInfo.order_status === 2) {
      completePayment()
      return
    }

    startCompleteCoolingTime()
  } catch (error) {
    if (!disposed) emit('error', error)
  } finally {
    loading.value = false
  }
}

onBeforeUnmount(() => {
  disposed = true
  stopPolling()
  if (qrCodeCountdownTimer) clearTimeout(qrCodeCountdownTimer)
  if (completeCoolingTimer) clearInterval(completeCoolingTimer)
})
</script>

<template>
  <div :class="$style.qrPaymentContainer">
    <div :class="$style.qrPaymentContainerInner">
      <!-- 二维码容器 -->
      <div v-if="qrCodeUrl" :class="$style.qrCodeContainer">
        <div :class="$style.qrCodeHeader">{{ paymentAmount }}</div>

        <div :class="$style.qrCodeImageContainer">
          <img :class="$style.qrCodeImage" :src="qrCodeUrl" alt="qr-code" />

          <div v-if="qrCodeCountdown <= 0" :class="$style.qrCodeFailureMask">
            <img :class="$style.qrCodeFailureMaskIcon" src="../../assets/img/failure_mark.png" alt="qr-code-failure" />
            <p :class="$style.qrCodeFailureMaskText">二维码失效</p>
          </div>
        </div>

        <div :class="$style.tipsContainer">
          <p v-if="qrCodeCountdown > 0">
            请在 <span :class="$style.tipsCountdown">{{ qrCodeCountdownFormat }}</span> 时间内扫一扫付款
          </p>
          <p v-else :class="$style.failureText">请更换支付方式或重新下单</p>
        </div>
      </div>

      <div v-if="loading" :class="$style.loadingContainer">
        <div :class="$style.loadingSpinner"></div>
      </div>

      <template v-if="qrCodeUrl">
        <button v-if="completeCoolingTime > 0" :class="$style.completeButton" disabled>
          尚未完成支付({{ completeCoolingTime }}s)
        </button>

        <button v-else-if="polling" :class="$style.completeButton" disabled>正在确认支付</button>

        <button v-else v-show="!loading" :class="$style.completeButton" @click="handleComplete">已完成支付</button>
      </template>

      <template v-else>
        <button v-if="!loading" :class="$style.payButton" @click="handlePay">{{ $t('t5') }} {{ paymentAmount }}</button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" module>
.qr-payment-container {
  width: 100%;

  button {
    --button-background-color: #4e5af0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    padding: 0 16px;
    min-width: 80px;
    background-color: var(--button-background-color);
    color: #fff;
    border: none;
    text-decoration: none;
    transition: background 0.3s ease-out, box-shadow 0.3s ease-out;

    height: 48px;
    width: 100%;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      --button-background-color: #6b73e6;
    }

    &[disabled] {
      cursor: not-allowed;
      // opacity: 0.25;
      --button-background-color: #c6cef8;
    }
  }

  .loadingContainer {
    margin-top: 32px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    .loadingSpinner {
      width: 40px;
      height: 40px;
      border: 3px solid #f0f0f0;
      border-top-color: #00112c;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  }

  .qr-payment-container-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .payButton {
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
  }

  .qrCodeContainer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 40px 40px 32px;

    border-radius: 12px;
    border: 1px solid #dcdfe6;
  }

  .qrCodeHeader {
    font-family: D-DIN-Bold, D-DIN;
    font-weight: 500;
    font-size: 36px;
    color: #4e5af0;
    line-height: 54px;
  }

  .qrCodeImageContainer {
    position: relative;
    width: 100%;
    max-width: 240px;
    padding: 20px;
    margin: 24px 0;
  }

  .qrCodeImage {
    width: 100%;
    height: auto;
  }

  .qrCodeFailureMask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    background-color: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);

    z-index: 10;

    .qrCodeFailureMaskIcon {
      width: 40px;
      height: 40px;
    }

    .qrCodeFailureMaskText {
      margin-top: 8px;
      font-weight: 400;
      font-size: 14px;
      color: #909399;
      line-height: 22px;
    }
  }

  .tipsContainer {
    font-size: 14px;
    color: #303133;
    line-height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;

    .tipsCountdown {
      color: #4e5af0;
    }

    .failureText {
      color: #f94e4e;
    }
  }

  .completeButton {
    width: 100%;
    margin-top: 24px;
    color: #ffffff;
    font-size: 16px;
    font-weight: 500;
    padding: 0 32px;
  }
}

:global(.mobile-mode) .qr-payment-container {
  .qrCodeContainer {
    padding: 24px 24px 28px;
  }
  .qrCodeImageContainer {
    margin: 12px 0;
  }
}
</style>
